import { createReducer, Action, on } from '@ngrx/store';

import { ExtractionRuleActions } from './extraction-rule.actions';
import { ExtractionRuleState } from './extraction-rule.state';
import { extractHttpErrorMessage } from '../../features/shared/utils/extract-http-error-message';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { DatapointViewMode, ResultViewMode } from '../../features/unified-datapoint/models/datapoint.model';

const RESULT_ERROR_FALLBACK = 'The request failed. The browser console has the details.';

export const initialState: ExtractionRuleState = {
  datapointsLoading: false,
  ruleLoading: null,
  extractionProgress: [],
  mode: DatapointViewMode.List,
  sort: { active: 'confidence', direction: 'desc' },
  isPolling: false,
  resultDatapoints: [],
  resultLoading: false,
  resultViewMode: ResultViewMode.Normal,
  imported: false,
  sectionSizes: {
    Rules: '25%',
    Progress: '25%',
    Result: '50%',
  },
  preview: true,
  groundTruthOnly: false,
  errorsOnly: false,
  experimenting: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      ExtractionRuleActions.LoadExtractionProgress,
      (state): ExtractionRuleState => ({ ...state, datapointsLoading: true, error: undefined })
    ),
    on(
      ExtractionRuleActions.LoadExtractionProgressSuccess,
      (state, action): ExtractionRuleState => ({
        ...state,
        datapointsLoading: false,
        extractionProgress: action.extractionProgress,
      })
    ),
    on(
      ExtractionRuleActions.LoadResult,
      (state): ExtractionRuleState => ({
        ...state,
        resultLoading: true,
        resultError: undefined,
        error: undefined,
        result: undefined,
      })
    ),
    on(
      ExtractionRuleActions.LoadResultSuccess,
      (state, action): ExtractionRuleState => ({
        ...state,
        resultDatapoints: action.datapoints,
        resultLoading: false,
        result: action.result,
        highlights: action.datapoints?.length === 1 ? action.datapoints[0]?.highlights : undefined,
      })
    ),
    on(
      ExtractionRuleActions.LoadResultFail,
      (state, action): ExtractionRuleState => ({
        ...state,
        resultLoading: false,
        result: [],
        resultError: extractHttpErrorMessage(action.error, RESULT_ERROR_FALLBACK),
      })
    ),
    on(
      ExtractionRuleActions.UpdateRuleBase,
      (state): ExtractionRuleState => ({ ...state, ruleLoading: true, error: undefined })
    ),
    on(
      ExtractionRuleActions.LoadDatapointRule,
      (state): ExtractionRuleState => ({
        ...state,
        ruleLoading: true,
        error: undefined,
        rule: undefined,
      })
    ),
    on(
      ExtractionRuleActions.UpdateDatapointRule,
      (state, action): ExtractionRuleState => ({
        ...state,
        ruleLoading: true,
        error: undefined,
        isPolling: !!action.run || state.isPolling,
      })
    ),
    on(
      ExtractionRuleActions.LoadDatapointRuleSuccess,
      ExtractionRuleActions.UpdateDatapointRuleSuccess,
      (state, action): ExtractionRuleState => ({
        ...state,
        ruleLoading: false,
        rule: action.rule,
        imported: false,
      })
    ),

    on(
      ExtractionRuleActions.UpdateRuleBaseSuccess,
      (state, action): ExtractionRuleState => ({
        ...state,
        ruleLoading: false,
        rule: state.rule?.id === action.id ? { ...(state.rule || {}), ...action.rule } : state.rule,
        imported: false,
      })
    ),
    on(
      ExtractionRuleActions.ImportRulesSuccess,
      (state, action): ExtractionRuleState => ({
        ...state,
        rule: { ...state.rule, rule: action.rules },
        imported: true,
      })
    ),
    on(
      ExtractionRuleActions.LoadExtractionProgressFail,
      ExtractionRuleActions.LoadDatapointRuleFail,
      ExtractionRuleActions.UpdateRuleBaseFail,
      (state, action): ExtractionRuleState => ({
        ...state,
        datapointsLoading: false,
        ruleLoading: false,
        error: action.error,
      })
    ),
    on(ExtractionRuleActions.SetViewMode, (state, action): ExtractionRuleState => ({ ...state, mode: action.mode })),
    on(ExtractionRuleActions.SetSort, (state, action): ExtractionRuleState => ({ ...state, sort: action.sort })),
    on(
      ExtractionRuleActions.SetSectionSizes,
      (state, action): ExtractionRuleState => ({ ...state, sectionSizes: action.sizes })
    ),
    on(ExtractionRuleActions.StartPolling, (state, action): ExtractionRuleState => ({ ...state, isPolling: true })),
    on(
      ExtractionRuleActions.StopPolling,
      ExtractionRuleActions.LoadExtractionProgressFail,
      (state, action): ExtractionRuleState => ({ ...state, isPolling: false })
    ),
    on(
      ExtractionRuleActions.DeleteDatapointsSuccess,
      (state, action): ExtractionRuleState => ({ ...state, extractionProgress: [] })
    ),
    on(
      ExtractionRuleActions.SetResultMode,
      (state, action): ExtractionRuleState => ({ ...state, resultViewMode: action.mode })
    ),
    on(
      ExtractionRuleActions.SetPreview,
      (state, action): ExtractionRuleState => ({ ...state, preview: action.preview })
    ),
    on(
      ExtractionRuleActions.SetGroundTruthOnly,
      (state, action): ExtractionRuleState => ({
        ...state,
        groundTruthOnly: action.groundTruthOnly,
      })
    ),
    on(
      ExtractionRuleActions.SetErrorsOnly,
      (state, action): ExtractionRuleState => ({ ...state, errorsOnly: action.errorsOnly })
    ),
    on(
      ExtractionRuleActions.RunExperiment,
      (state, action): ExtractionRuleState => ({ ...state, experimenting: true })
    ),
    on(
      ExtractionRuleActions.RunExperimentSuccess,
      (state, action): ExtractionRuleState => ({
        ...state,
        rule: { ...state.rule, experiment: action.experiment },
        experimenting: false,
      })
    ),
    on(
      ExtractionRuleActions.RunExperimentFail,
      (state, action): ExtractionRuleState => ({
        ...state,
        error: action.error,
        experimenting: false,
      })
    )
  )
);

export function reducer(state: ExtractionRuleState | undefined, action: Action) {
  return featureReducer(state, action);
}
